import {
  CircleCheck,
  CircleDot,
  CircleX,
  FileCheck,
  FilePlus,
  Hourglass,
  LucideIcon,
  Send,
  Undo2,
} from "lucide-react";

import { Badge, BadgeProps } from "components/ui/badge";
import { CopyButton } from "components/ui/copy-button";
import { Case } from "ts/interfaces/common/case";
import { renderLongDate } from "utils/helpers";
import { CaseApplication } from "../types";

const STATUS_STYLES: Record<
  string,
  { icon: LucideIcon; variant: BadgeProps["variant"] }
> = {
  STARTED: { icon: FilePlus, variant: "secondary" },
  APP_IN_PROGRESS: { icon: Hourglass, variant: "secondary" },
  APP_SUBMITTED: { icon: Send, variant: "success" },
  OFFER_ISSUED: { icon: FileCheck, variant: "success" },
  CASE_COMPLETE: { icon: CircleCheck, variant: "success" },
  APP_WITHDRAWN: { icon: Undo2, variant: "warning" },
  OFFER_REJECTED: { icon: CircleX, variant: "danger" },
};

const DEFAULT_STATUS_STYLE = { icon: CircleDot, variant: "secondary" } as const;

interface CaseHeaderProps {
  caseDetail: Case;
  latestSubmission?: CaseApplication;
}

const CaseHeader: React.FC<CaseHeaderProps> = ({
  caseDetail,
  latestSubmission,
}) => {
  const meta = [
    caseDetail.createdOn && `Created ${renderLongDate(caseDetail.createdOn)}`,
  ].filter(Boolean);

  const statusStyle = STATUS_STYLES[caseDetail.status] ?? DEFAULT_STATUS_STYLE;
  const StatusIcon = statusStyle.icon;

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1">
          <div className="group flex items-center gap-1">
            <h1 className="text-3xl font-medium tracking-tight text-brand-copy-primary">
              {caseDetail.reference}
            </h1>
            <CopyButton
              value={caseDetail.reference}
              label="Copy case reference"
            />
          </div>
          {caseDetail.statusDisplay && (
            <Badge
              variant={statusStyle.variant}
              className="ml-2 gap-2 px-3 py-1 text-sm"
            >
              <StatusIcon aria-hidden="true" className="size-4" />
              {caseDetail.statusDisplay}
            </Badge>
          )}
        </div>

        {caseDetail.applicantNames && (
          <p className="text-lg text-brand-copy-primary">
            {caseDetail.applicantNames}
          </p>
        )}
        {meta.length > 0 && (
          <p className="text-sm text-muted-foreground">{meta.join(" | ")}</p>
        )}
      </div>
    </div>
  );
};

export default CaseHeader;
